import {
  Grid,
  IconButton,
  TextField,
  LinearProgress,
  Card,
  CardContent,
  Stack,
  Typography,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import Box from "@mui/material/Box";
import { useTheme } from "@emotion/react";
import { DataGrid, GridPagination } from "@mui/x-data-grid";
import { BiTrash, BiEdit } from "react-icons/bi";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Investors,
  setBalanceDialog,
  setInvestorsTransactionsDialog,
  setSearchBody,
} from "../../../../../app/slices/investorsSlice";
import dayjs from "dayjs";
import ConfirmationDialog from "../../../../../helpers/ConfirmationDialog";
import InitialInvestorsBalanceForm from "../../form/balance";
import { GiPayMoney, GiReceiveMoney } from "react-icons/gi";
import { MdOutlineClear } from "react-icons/md";
import { FaMoneyCheckAlt } from "react-icons/fa";
import Permission from "../../../../../components/userPermission/userPermission";
import { HiOutlineUserGroup, HiOutlineBanknotes } from "react-icons/hi2";
import {
  FILTERS_SX,
  MobileAction,
  MobileEmpty,
  MobileListItem,
  MobileListShell,
  MobilePagination,
  MobileSummaryCard,
  TONE,
} from "../../../../../components/MobileList";
import {
  EditableField,
  useInlineEditConfig,
} from "../../../../../components/MobileList/inlineEdit";
import investorsInlineEdit from "./inlineEdit";

const InvestorsTable = () => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const _Investors = useSelector((state) => state.investors.investors);
  const loading = useSelector((state) => state.investors.investors.loading);
  const _SearchBody = useSelector((state) => state.investors.searchBody);
  const _InitialInvestorsBalance = useSelector(
    (state) => state.investors.balance.dialog,
  );

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(0);
  const [openConfirm, setConfirmOpen] = useState(false);
  const [dialogData, setDialogData] = useState("");

  const handleDeleteClick = (params) => {
    setConfirmOpen(true);
    setDialogData(params);
  };

  const handleConfirmDelete = () => {
    dispatch(Investors.deleteById(dialogData.id, _SearchBody));
    setConfirmOpen(false);
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
  };

  const handlePageChange = (newPage) => {
    dispatch(
      Investors.getAll({
        search: search,
        skip: newPage * page,
        take: page,
      }),
    );
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    dispatch(
      Investors.getAll({
        search: search,
        skip: 0,
        take: newPageSize,
      }),
    );
    setPage(newPageSize);
    setCurrentPage(0);
  };

  const inlineEdit = useInlineEditConfig(
    investorsInlineEdit,

    () =>
      dispatch(
        Investors.getAll({
          search: search,
          skip: currentPage * page,
          take: page,
        }),
      ),

    [dispatch, search, currentPage, page],
  );

  const columns = [
    {
      field: "index",
      headerName: "#",
      flex: 0.2,
    },
    {
      field: "name",
      headerName: "الاسم",
      flex: 1,
    },
    {
      field: "balance",
      headerName: "الرصيد",
      flex: 1,
      renderCell: (params) => parseInt(params.row.balance).toLocaleString(),
    },
    {
      field: "note",
      headerName: "الملاحظات",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.row.note}>
          <Typography variant="body" noWrap>
            {params.row.note}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "created_at",
      headerName: "التاريخ",
      flex: 1,
      renderCell: (params) =>
        dayjs(params.row.created_at).format("HH:mm / YYYY-MM-DD "),
    },
    {
      field: "operations",
      headerName: "العمليات",
      flex: 1,
      renderCell: (params) => {
        return (
          <>
            <Permission permission="investors_edit">
              <Tooltip title="اضافة رصيد">
                <IconButton
                  variant="contained"
                  color="info"
                  onClick={() =>
                    dispatch(
                      setBalanceDialog({
                        id: params.row.id,
                        type: "deposit",
                      }),
                    )
                  }
                >
                  <GiReceiveMoney />
                </IconButton>
              </Tooltip>
            </Permission>
            <Permission permission="investors_edit">
              <Tooltip title="سحب رصيد">
                <IconButton
                  variant="contained"
                  color="error"
                  onClick={() =>
                    dispatch(
                      setBalanceDialog({
                        id: params.row.id,
                        type: "withdraw",
                      }),
                    )
                  }
                >
                  <GiPayMoney />
                </IconButton>
              </Tooltip>
            </Permission>
            <Tooltip title="المعاملات المالية">
              <IconButton
                variant="contained"
                onClick={() =>
                  dispatch(setInvestorsTransactionsDialog(params.row.id))
                }
              >
                <FaMoneyCheckAlt />
              </IconButton>
            </Tooltip>
            <Permission permission="investors_edit">
              <Tooltip title="تعديل">
                <IconButton
                  onClick={() => dispatch(Investors.getById(params.row.id))}
                >
                  <BiEdit />
                </IconButton>
              </Tooltip>
            </Permission>
            <Permission permission="investors_delete">
              <Tooltip title="حذف">
                <IconButton onClick={() => handleDeleteClick(params.row)}>
                  <BiTrash />
                </IconButton>
              </Tooltip>
            </Permission>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => {
      dispatch(Investors.getAll({ search: search, skip: 0, take: page }));
      dispatch(setSearchBody({ search: search, skip: 0, take: page }));
      setCurrentPage(0);
    }, 500);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, search]);

  function CustomFooter() {
    return (
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography color="primary" sx={{ marginX: 2 }}>
          المجموع الكلي : {_Investors.total_amount.toLocaleString()} (IQD)
        </Typography>
        <GridPagination />
      </Stack>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box>
          {_InitialInvestorsBalance && <InitialInvestorsBalanceForm />}
          <ConfirmationDialog
            open={openConfirm}
            title="تاكيد"
            message="هل انت متاكد من حذف"
            name={dialogData.name}
            confirmText="حذف"
            cancelText="الغاء"
            onConfirm={() => handleConfirmDelete()}
            onCancel={handleCancelDelete}
          />
        </Box>
        <Grid
          container
          rowSpacing={1}
          columnSpacing={2}
          sx={smallScreen ? FILTERS_SX : undefined}
        >
          <Grid item xs={10} md={3}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              label="بحث عن الاسم "
            />
          </Grid>
          <Grid item xs={2} md={2.5}>
            <Stack direction="row" spacing={0} sx={{ alignItems: "center" }}>
              <Tooltip title="الغاء الفلتر">
                <IconButton
                  onClick={() => {
                    setSearch("");
                    setPage(15);
                    setCurrentPage(0);
                  }}
                >
                  <MdOutlineClear />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          {smallScreen ? (
            <Grid item xs={12}>
              <MobileListShell loading={loading}>
                <MobileSummaryCard
                  value={(_Investors.total_amount ?? 0).toLocaleString()}
                  icon={<HiOutlineBanknotes />}
                />
                {_Investors.data?.length > 0 ? (
                  <>
                    {_Investors.data.map((investor, index) => (
                      <MobileListItem
                        // Keyed by id so a refetch cannot leave an open editor
                        // sitting over a different record.
                        key={investor.id ?? index}
                        avatar={<HiOutlineUserGroup />}
                        title={
                          <EditableField
                            config={inlineEdit}
                            field="name"
                            row={investor}
                            display={investor?.name}
                            variant="title"
                          />
                        }
                        subtitle={dayjs(investor.created_at).format(
                          "YYYY-MM-DD · HH:mm",
                        )}
                        highlight={{
                          label: "الرصيد",
                          value: parseInt(
                            investor?.balance ?? 0,
                          ).toLocaleString(),
                          rawValue: investor?.balance,
                          unit: "IQD",
                          tone: "auto",
                        }}
                        note={
                          <EditableField
                            config={inlineEdit}
                            field="note"
                            row={investor}
                            display={investor?.note}
                            variant="note"
                          />
                        }
                        actions={
                          <>
                            <Permission permission="investors_edit">
                              <MobileAction
                                title="اضافة رصيد"
                                tone={TONE.success}
                                icon={<GiReceiveMoney />}
                                onClick={() =>
                                  dispatch(
                                    setBalanceDialog({
                                      id: investor.id,
                                      type: "deposit",
                                    }),
                                  )
                                }
                              />
                            </Permission>
                            <Permission permission="investors_edit">
                              <MobileAction
                                title="سحب رصيد"
                                tone={TONE.danger}
                                icon={<GiPayMoney />}
                                onClick={() =>
                                  dispatch(
                                    setBalanceDialog({
                                      id: investor.id,
                                      type: "withdraw",
                                    }),
                                  )
                                }
                              />
                            </Permission>
                            <MobileAction
                              title="المعاملات المالية"
                              icon={<FaMoneyCheckAlt />}
                              onClick={() =>
                                dispatch(
                                  setInvestorsTransactionsDialog(investor.id),
                                )
                              }
                            />
                            <Permission permission="investors_edit">
                              <MobileAction
                                title="تعديل"
                                tone={TONE.warning}
                                icon={<BiEdit />}
                                onClick={() =>
                                  dispatch(Investors.getById(investor.id))
                                }
                              />
                            </Permission>
                            <Permission permission="investors_delete">
                              <MobileAction
                                title="حذف"
                                tone={TONE.danger}
                                icon={<BiTrash />}
                                onClick={() => handleDeleteClick(investor)}
                              />
                            </Permission>
                          </>
                        }
                      />
                    ))}
                    <MobilePagination
                      count={_Investors.total ?? 0}
                      rowsPerPage={page}
                      page={currentPage}
                      onPageChange={(e, newPage) => handlePageChange(newPage)}
                      rowsPerPageOptions={[15, 25, 50, 100]}
                      onRowsPerPageChange={(event) => {
                        handlePageSizeChange(parseInt(event.target.value, 10));
                      }}
                    />
                  </>
                ) : (
                  <MobileEmpty />
                )}
              </MobileListShell>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Box sx={{ height: "70vh", width: "100%" }}>
                <DataGrid
                  rows={
                    _Investors.data?.map((x, index) => ({
                      ...x,
                      index: index + 1,
                    })) ?? []
                  }
                  columns={columns}
                  pageSize={page}
                  page={currentPage}
                  rowCount={_Investors.total}
                  loading={loading}
                  rowHeight={50}
                  pagination
                  paginationMode="server"
                  selectionModel={[]}
                  onPageChange={(newPage) => handlePageChange(newPage)}
                  rowsPerPageOptions={[15, 25, 50, 100]}
                  onPageSizeChange={(newPageSize) =>
                    handlePageSizeChange(newPageSize)
                  }
                  components={{
                    LoadingOverlay: LinearProgress,
                    Footer: CustomFooter,
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default InvestorsTable;
